// ═══════════════════════════════════════════════════════════════
// useAuth.js — verticalstack auth hook with workspace hydration (v4)
// Path: src/hooks/useAuth.js
//
// S31 LAYER 1 (Apr 27, 2026) — sandbox-schema migration:
//
//   v4 changes from v3:
//     - Profile shape: clientId → workspaceId; role(string) → roles(array)
//     - pickPrimaryWorkspace: drop ClientAdmin preference (AS-I).
//       New order: isHomeWorkspace → roles.includes('owner_pm') → first.
//     - getUserWorkspaces CF response shape changes:
//         OLD: { workspaces: [{ clientId, role, clientName, billingTier, vertical, ... }] }
//         NEW: { workspaces: [{ workspaceId, roles, workspaceName, plan, verticalType, ... }] }
//       (See get_user_workspaces_v2.js — Row 13 of Migration Matrix v1.1.)
//     - switchWorkspace removed — workspace state moves to useWorkspace hook.
//     - isPlatformAdmin added — reads platformAdmins/{uid} doc existence (AS-I).
//
//   Profile shape after hydration (v4):
//     {
//       id:             firebase.auth uid
//       email, displayName, ...      (fields from users/{uid})
//       workspaces:     [ { workspaceId, roles, status, workspaceName,
//                           plan, verticalType, isHomeWorkspace }, ... ]
//       primaryWorkspaceId:          (informational — picked by pickPrimaryWorkspace)
//       homeWorkspaceId:             (from users/{uid}.homeWorkspaceId or first home in list)
//       isPlatformAdmin:             true if platformAdmins/{uid} exists (AS-I)
//     }
//
//   NOTE: profile no longer carries a top-level role or workspaceId.
//   Components needing role + workspace context use useWorkspace +
//   useMembership (added in Rows 3 and 4 of the migration matrix).
//
//   getUserWorkspaces is still called via httpsCallable — function name
//   preserved per AS-L. Only the response shape changed; the wire protocol
//   is identical.
//
// History (preserved for context):
//   v2: direct collectionGroup query — failed with permissions (AS-208)
//   v3 (S16C): switched to getUserWorkspaces CF — bypassed rules
//   v4 (S31): new-schema response shape, role array, no clientId
// ═══════════════════════════════════════════════════════════════

import { createContext, useContext, useEffect, useState } from 'react';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
} from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { auth, db, functions } from '../firebase/config';

const AuthContext = createContext(null);

// ─────────────────────────────────────────────────────────────
// Hydrate workspace memberships via Cloud Function.
// Returns array of { workspaceId, roles, status, workspaceName, ... }.
// Never throws — on failure returns empty array and logs.
// ─────────────────────────────────────────────────────────────
async function fetchUserWorkspaces() {
  try {
    const getUserWorkspaces = httpsCallable(functions, 'getUserWorkspaces');
    const result = await getUserWorkspaces();
    const workspaces = result?.data?.workspaces || [];
    return Array.isArray(workspaces) ? workspaces : [];
  } catch (err) {
    console.error('[useAuth] getUserWorkspaces CF call failed:', err);
    return [];
  }
}

// ─────────────────────────────────────────────────────────────
// Check whether the current user is a platform admin.
// Per AS-I: admin authority comes from platformAdmins/{uid} doc
// existence, NOT from a clientMembers role. Bypasses workspace scoping.
// ─────────────────────────────────────────────────────────────
async function fetchIsPlatformAdmin(uid) {
  if (!uid) return false;
  try {
    const adminSnap = await getDoc(doc(db, 'platformAdmins', uid));
    return adminSnap.exists();
  } catch (err) {
    // platformAdmins is read-restricted by rules; absence of doc OR
    // permission-denied both resolve to "not an admin".
    console.warn('[useAuth] platformAdmins check failed (treating as non-admin):', err.message);
    return false;
  }
}

// ─────────────────────────────────────────────────────────────
// Pick the "primary" workspace out of a list.
// Used to seed initial workspace selection before useWorkspace
// reads localStorage and overrides with the user's last choice.
//
// Preference order (v4):
//   1. Home workspace (isHomeWorkspace === true)
//   2. First workspace where roles includes 'owner_pm'
//   3. First workspace in the list
//
// Note: ClientAdmin preference dropped (AS-I — ClientAdmin retired).
// ─────────────────────────────────────────────────────────────
function pickPrimaryWorkspace(workspaces) {
  if (!workspaces || workspaces.length === 0) return null;
  const home = workspaces.find((w) => w.isHomeWorkspace);
  if (home) return home;
  const ownerPm = workspaces.find(
    (w) => Array.isArray(w.roles) && w.roles.includes('owner_pm')
  );
  if (ownerPm) return ownerPm;
  return workspaces[0];
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (firebaseUser) => {
      if (!firebaseUser) {
        setUser(null);
        setProfile(null);
        setLoading(false);
        return;
      }

      setUser(firebaseUser);

      try {
        // Step 1 — load users/{uid} for global identity fields
        const userSnap = await getDoc(doc(db, 'users', firebaseUser.uid));
        const userData = userSnap.exists()
          ? { id: userSnap.id, ...userSnap.data() }
          : { id: firebaseUser.uid };

        // Step 2 — hydrate workspaces via Cloud Function (v2 reads new schema)
        const workspaces = await fetchUserWorkspaces();
        const primary = pickPrimaryWorkspace(workspaces);

        // Step 3 — check platform admin status (AS-I)
        const isPlatformAdmin = await fetchIsPlatformAdmin(firebaseUser.uid);

        setProfile({
          ...userData,
          workspaces,
          primaryWorkspaceId: primary ? primary.workspaceId : null,
          homeWorkspaceId:
            userData.homeWorkspaceId ||
            (primary && primary.isHomeWorkspace ? primary.workspaceId : null) ||
            null,
          isPlatformAdmin,
        });
      } catch (err) {
        console.error('[useAuth] profile hydration failed:', err);
        setProfile({
          id: firebaseUser.uid,
          email: firebaseUser.email,
          workspaces: [],
          primaryWorkspaceId: null,
          homeWorkspaceId: null,
          isPlatformAdmin: false,
        });
      } finally {
        setLoading(false);
      }
    });

    return unsub;
  }, []);

  const login = (email, password) =>
    signInWithEmailAndPassword(auth, email, password);

  const logout = () => signOut(auth);

  const resetPassword = (email) => sendPasswordResetEmail(auth, email);

  // Admin creates accounts for owners and tenants.
  // Writes to /users/{uid} — collection unchanged in sandbox schema.
  // Workspace membership creation happens through invitation flow
  // (Layer 2+, when inviteUser CF is migrated).
  const createAccount = async (email, password, profileData) => {
    const cred = await createUserWithEmailAndPassword(auth, email, password);
    await setDoc(doc(db, 'users', cred.user.uid), {
      ...profileData,
      email,
      createdAt: new Date(),
    });
    return cred;
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        profile,
        loading,
        login,
        logout,
        resetPassword,
        createAccount,
        // NOTE: switchWorkspace removed in v4 — workspace state lives in useWorkspace.
      }}
    >
      {!loading && children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);